import { Controller } from "@hotwired/stimulus"

// Manages portal brief groups configuration with drag-and-drop
export default class extends Controller {
  static targets = ["output", "groupsContainer", "dropzone", "pool", "groupTemplate", "groupName", "groupDescription", "emptyMessage"]

  connect() {
    this.draggedElement = null
    this.initializeDropzones()
  }

  initializeDropzones() {
    // Set up drag events for existing dropzones
    this.dropzoneTargets.forEach(dropzone => {
      dropzone.addEventListener('dragover', this.dragOver.bind(this))
      dropzone.addEventListener('drop', this.dropInGroup.bind(this))
      dropzone.addEventListener('dragleave', this.dragLeave.bind(this))
    })
  }

  // Add a new empty group
  addGroup() {
    const template = this.groupTemplateTarget.content.cloneNode(true)
    const groupElement = template.querySelector('.group-item')

    // Set group index
    const existingGroups = this.groupsContainerTarget.querySelectorAll('.group-item')
    groupElement.dataset.groupIndex = existingGroups.length

    // Add drag events to the new dropzone
    const dropzone = groupElement.querySelector('[data-groups-config-target="dropzone"]')
    dropzone.addEventListener('dragover', this.dragOver.bind(this))
    dropzone.addEventListener('drop', this.dropInGroup.bind(this))
    dropzone.addEventListener('dragleave', this.dragLeave.bind(this))

    // Hide empty message if present
    if (this.hasEmptyMessageTarget) {
      this.emptyMessageTarget.classList.add('hidden')
    }

    this.groupsContainerTarget.appendChild(groupElement)
    this.updateOutput()
  }

  // Remove a group and return its briefs to the pool
  removeGroup(event) {
    const groupElement = event.target.closest('.group-item')
    if (!groupElement) return

    // Move all briefs back to pool
    const briefCards = groupElement.querySelectorAll('.brief-card')
    briefCards.forEach(card => {
      this.poolTarget.appendChild(card)
    })

    groupElement.remove()

    // Show empty message if no groups remain
    const remainingGroups = this.groupsContainerTarget.querySelectorAll('.group-item')
    if (remainingGroups.length === 0 && this.hasEmptyMessageTarget) {
      this.emptyMessageTarget.classList.remove('hidden')
    }

    this.updateOutput()
  }

  // Drag start
  dragStart(event) {
    this.draggedElement = event.target
    event.target.classList.add('opacity-50')
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', event.target.dataset.briefId)
  }

  // Drag end
  dragEnd(event) {
    event.target.classList.remove('opacity-50')
    this.draggedElement = null

    // Remove all drag-over styles
    document.querySelectorAll('.drag-over').forEach(el => {
      el.classList.remove('drag-over', 'bg-primary-50', 'dark:bg-primary-900/20')
    })
  }

  // Drag over a dropzone
  dragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'

    const dropzone = event.currentTarget
    if (!dropzone.classList.contains('drag-over')) {
      dropzone.classList.add('drag-over', 'bg-primary-50', 'dark:bg-primary-900/20')
    }
  }

  // Drag leave
  dragLeave(event) {
    const dropzone = event.currentTarget
    // Only remove style if we're leaving the dropzone entirely
    if (!dropzone.contains(event.relatedTarget)) {
      dropzone.classList.remove('drag-over', 'bg-primary-50', 'dark:bg-primary-900/20')
    }
  }

  // Drop in a group
  dropInGroup(event) {
    event.preventDefault()
    const dropzone = event.currentTarget
    dropzone.classList.remove('drag-over', 'bg-primary-50', 'dark:bg-primary-900/20')

    if (this.draggedElement && dropzone !== this.draggedElement.parentElement) {
      dropzone.appendChild(this.draggedElement)
      this.updateOutput()
    }
  }

  // Drop in the pool (unassign brief)
  dropInPool(event) {
    event.preventDefault()
    this.poolTarget.classList.remove('drag-over', 'bg-primary-50', 'dark:bg-primary-900/20')

    if (this.draggedElement && this.poolTarget !== this.draggedElement.parentElement) {
      this.poolTarget.appendChild(this.draggedElement)
      this.updateOutput()
    }
  }

  // Update the hidden output field with current configuration
  updateOutput() {
    const groups = []
    const groupElements = this.groupsContainerTarget.querySelectorAll('.group-item')

    groupElements.forEach(groupElement => {
      const nameInput = groupElement.querySelector('[data-groups-config-target="groupName"]')
      const descInput = groupElement.querySelector('[data-groups-config-target="groupDescription"]')
      const dropzone = groupElement.querySelector('[data-groups-config-target="dropzone"]')
      const briefCards = dropzone.querySelectorAll('.brief-card')

      const briefIds = Array.from(briefCards).map(card => card.dataset.briefId)

      groups.push({
        name: nameInput?.value || '',
        description: descInput?.value || '',
        brief_ids: briefIds
      })
    })

    this.outputTarget.value = JSON.stringify(groups)
  }
}
